import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Points } from '@styles';
import { Colors } from 'planoplan-ui-kit/core/colors';

export const Pagination = ({ currentPage, totalPages, startUrl, locale = 'en' }) => {
  const [isMobile, setIsMobile] = useState(true);

  const linkSigns = {
    en: {
      prev: 'Previous page',
      next: 'Next page',
      page: 'page',
    },
    ru: {
      prev: 'Предыдущая страница',
      next: 'Следующая страница',
      page: 'страница',
    },
    de: {
      prev: 'Vorherige Seite',
      next: 'Nächste Seite',
      page: 'seite',
    },
    es: {
      prev: 'Página anterior',
      next: 'Página siguiente',
      page: 'página',
    },
  };

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 1024);
    }

    // Установить начальные размеры
    handleResize();

    // Подписаться на ресайз
    window.addEventListener('resize', handleResize);

    // Очистить при размонтировании
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getPagination = (current, total) => {
    const pages = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }
    if (current <= 3) {
      pages.push(1, 2, 3, 4, '...', total);
    } else if (current >= total - 2) {
      pages.push(1, '...', total - 3, total - 2, total - 1, total);
    } else {
      pages.push(1, '...', current - 1, current, current + 1, '...', total);
    }
    return pages;
  };

  const pages = getPagination(currentPage, totalPages);

  return (
    <Container>
      {currentPage > 1 ? (
        <Link href={`${startUrl}?page=${currentPage - 1}`} key={`${currentPage - 1}`} passHref>
          <PrevLink aria-label={`${linkSigns[locale].prev}`}>
            <SvgWrap>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 12L3 12M3 12L8.67157 18M3 12L8.67157 6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </SvgWrap>
            {!isMobile && linkSigns[locale].prev}
          </PrevLink>
        </Link>
      ) : (
        <PrevLink />
      )}
      <PagesList>
        {pages.map((item, index) => {
          return currentPage === item || item === '...' ? (
            <Page isCurrent={currentPage === item} key={`${item}-${index}`}>
              {item === '...' ? '...' : item}
            </Page>
          ) : (
            <Page key={`${item}-${index}`}>
              <Link href={`${startUrl}?page=${item}`} passHref>
                <a aria-label={`${linkSigns[locale].page} ${item}`}>{item}</a>
              </Link>
            </Page>
          );
        })}
      </PagesList>
      {currentPage < totalPages ? (
        <Link href={`${startUrl}?page=${currentPage + 1}`} key={`${currentPage + 1}`} passHref>
          <NextLink aria-label={`${linkSigns[locale].next}`}>
            {!isMobile && linkSigns[locale].next}
            <SvgWrap>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M4 12L21 12M21 12L15.3284 6M21 12L15.3284 18" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </SvgWrap>
          </NextLink>
        </Link>
      ) : (
        <NextLink />
      )}
    </Container>
  );
};

const Container = styled.nav`
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  margin-top: 80px;

  @media ${Points.tablet_landscape} {
    margin-top: 100px;
  }

  @media ${Points.laptop_wide} {
    margin-top: 120px;
  }

  @media ${Points.fullhd} {
    margin-top: 160px;
  }
`;

const PrevLink = styled.a`
  ${Mixins.resetLink()};
  display: flex;
  align-self: center;
  gap: 5px;
  grid-column: 1;
  grid-row: 1;
  justify-items: left;

  @media ${Points.tablet_landscape} {
    padding-top: 6px;
  }

  @media ${Points.laptop_wide} {
    padding-top: 7px;
  }

  & svg {
    stroke: ${Colors.graphite};
  }

  &:hover {
    color: ${Colors.second_blue};

    & svg {
      stroke: ${Colors.second_blue};
    }
  }

  &:active {
    color: ${Colors.main_blue};

    & svg {
      stroke: ${Colors.main_blue};
    }
  }
`;

const SvgWrap = styled.span`
  position: relative;
  top: -1px;
`;

const NextLink = styled.a`
  ${Mixins.resetLink()};
  display: flex;
  align-self: center;
  gap: 5px;
  grid-column: 3;
  grid-row: 1;
  justify-self: end;
  width: fit-content;

  @media ${Points.tablet_landscape} {
    padding-top: 6px;
  }

  @media ${Points.laptop_wide} {
    padding-top: 7px;
  }

  & svg {
    stroke: ${Colors.graphite};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${Colors.second_blue};

      & svg {
        stroke: ${Colors.second_blue};
      }
    }
  }

  &:active {
    color: ${Colors.main_blue};

    & svg {
      stroke: ${Colors.main_blue};
    }
  }
`;

const PagesList = styled.ul`
  ${Mixins.resetList()};
  display: flex;
  justify-content: center;
  gap: 2px;
  margin: 0 auto;
  grid-column: 2;
  grid-row: 1;
  justify-items: center;
  width: 100%;
`;

const Page = styled.li`
  display: block;
  width: 40px;
  height: 40px;
  padding: 11px 0;
  background-color: ${(p) => p.isCurrent && Colors.blue_200_50};
  border-radius: ${(p) => p.isCurrent && '10px'};
  color: ${(p) => (p.isCurrent ? Colors.main_blue : Colors.main_grey)};
  text-align: center;
  font-size: 16px;
  line-height: 110%;

  @media ${Points.fullhd} {
    font-size: 18px;
  }

  &:hover {
    background-color: ${(p) => !p.isCurrent && Colors.blue_200_50};
    border-radius: ${(p) => !p.isCurrent && '10px'};
  }

  &:active {
    background-color: ${(p) => !p.isCurrent && Colors.blue_200_50};
    color: ${(p) => !p.isCurrent && Colors.main_blue};
  }

  & a {
    ${Mixins.resetLink()};
  }
`;
